import type { IsoDate, IsoWeekday, RotationPeriod, TaskDefinitionInput } from './types.ts'
import { floorMod, weekStartFor, weeksBetween, addDays } from './dates.ts'

export interface RotationTurnParams {
  members: readonly string[]
  period: RotationPeriod
  anchorDate: IsoDate
  weekStart: IsoDate
  weekStartDay: IsoWeekday
  /** Posicion de la ocurrencia dentro de su semana. Solo en BY_OCCURRENCE. */
  indexInWeek?: number
  /** Ocurrencias que genera la tarea en una semana. Solo en BY_OCCURRENCE. */
  occurrencesPerWeek?: number
}

/**
 * Resuelve a quien le toca una tarea rotativa (§6.7).
 *
 * El turno se deriva por aritmetica modular desde una fecha ancla, en vez de
 * llevar un contador guardado. Asi es reproducible, se puede calcular hacia el
 * futuro para mostrar "la semana que viene te toca a ti", y no se desincroniza
 * si una semana falla al materializarse.
 *
 * Contrapartida documentada: anadir o quitar un miembro desplaza todos los
 * turnos posteriores.
 */
export function resolveRotationTurn(params: RotationTurnParams): string {
  const { members, period, anchorDate, weekStart, weekStartDay } = params
  if (members.length === 0) {
    throw new RangeError('Una tarea rotativa necesita al menos un miembro')
  }

  // El ancla puede ser cualquier dia; se normaliza al inicio de su semana para
  // que un ancla mal puesta no desplace la rotacion entera.
  const anchorWeekStart = weekStartFor(anchorDate, weekStartDay)
  const weeks = weeksBetween(anchorWeekStart, weekStart)

  if (period === 'WEEKLY') {
    return members[floorMod(weeks, members.length)]
  }

  const occurrencesPerWeek = params.occurrencesPerWeek ?? 1
  const indexInWeek = params.indexInWeek ?? 0
  if (!Number.isInteger(occurrencesPerWeek) || occurrencesPerWeek < 1) {
    throw new RangeError(`occurrencesPerWeek invalido: ${occurrencesPerWeek}`)
  }
  const globalIndex = weeks * occurrencesPerWeek + indexInWeek
  return members[floorMod(globalIndex, members.length)]
}

export interface RotationPreviewEntry {
  weekStart: IsoDate
  userId: string
}

/**
 * Proximos turnos de una tarea rotativa, para el editor del admin (§10.3).
 * La aritmetica modular es dificil de predecir mentalmente y ver los siguientes
 * turnos evita configurar mal el ancla.
 */
export function previewRotation(
  def: Pick<TaskDefinitionInput, 'rotationMembers' | 'rotationPeriod' | 'rotationAnchorDate'>,
  fromWeekStart: IsoDate,
  weekStartDay: IsoWeekday,
  count = 4,
): RotationPreviewEntry[] {
  const members = def.rotationMembers ?? []
  if (members.length === 0) return []

  return Array.from({ length: count }, (_, i) => {
    const weekStart = addDays(fromWeekStart, i * 7)
    return {
      weekStart,
      userId: resolveRotationTurn({
        members,
        period: def.rotationPeriod ?? 'WEEKLY',
        anchorDate: def.rotationAnchorDate ?? fromWeekStart,
        weekStart,
        weekStartDay,
      }),
    }
  })
}
